import { GridRowId } from "@mui/x-data-grid";
import { differenceInMinutes } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ASSIGN_SHIFT } from "../../../../../assets/constants";
import {
	filterShiftsByWorkerDay,
	formatFullTimeShifts,
} from "../../../../../utils/helpers";
import type {
	DirectAssignmentSlot,
	SelectedWorkerSlot,
	WorkerSlot,
} from "../../../types/direct-assignment";
import {
	countHalvesTaken,
	isWithinRequiredWorkers,
} from "../../../types/direct-assignment";
import {
	APPLIED,
	CANCELLED,
	DECLINED,
	NOT_NOTIFIED,
	VIEWED,
} from "../shift-details-dialog/constants";
import { NOTIFIED } from "../shift-details-dialog/constants";
import useShiftDetailsDialogColumns from "../shift-details-dialog/controllers/use-shift-details-dialog-columns";
import {
	WorkersExtendedPropV2,
	WorkersPropsV2,
} from "../shift-details-dialog/types";

// The Shift the Workers are being picked for. A draft row passes what
// WorkersV2 returns; Post Coverage passes the form's values.
export interface DirectAssignShift {
	startDateTime: string;
	endDateTime: string;
	numberOfWorkersNeeded: number;
	jobId?: number | string | null;
	shiftWorkcenterName?: string;
	shiftLocationName?: string;
	shiftLocationClusterName?: string | null;
	shiftAllocationType?: string;
	isPartialSignupEnabled?: boolean;
	isFlexiSignupEnabled?: boolean;
	minFlexiSignupHours?: number;
	isOverlappigSignupEnabled?: boolean;
	overlappingSignupLimitMins?: number;
	isShiftOpenForOtherLocation?: boolean;
}

// What the Shift Details picker keeps per Worker: a slot number, or a custom
// window for slot 3
type SlotSelection =
	| number
	| { slot: number; customStartTime: string; customEndTime: string };

// Same statuses the Shift Details Assign step offers. Workers from the
// coverage count call carry no status and are all eligible.
const PICKABLE_STATUSES = [
	NOTIFIED,
	VIEWED,
	NOT_NOTIFIED,
	DECLINED,
	CANCELLED,
	APPLIED,
];

// Columns that describe a published Shift's signups, not a pick
const HIDDEN_FIELDS = ["actions", "lastUpdatedUtc"];
const UNCHECKED_BY_DEFAULT_FIELDS = ["Status"];

// The Shift Details column definitions, plus the column picker's flag
interface PickerColumn {
	field: string;
	label: string;
	checked: boolean;
	[key: string]: unknown;
}

const noop = () => {
	// Nothing to remove before the Shift exists
};

const toSlotSelection = (
	selected: SelectedWorkerSlot
): SlotSelection =>
	selected.slot === 3 &&
	selected.workerShiftStartDateTime &&
	selected.workerShiftEndDateTime
		? {
			slot: 3,
			customStartTime: selected.workerShiftStartDateTime,
			customEndTime: selected.workerShiftEndDateTime,
		}
		: selected.slot;

const toWorkerSlot = (
	workerId: string,
	slotSelection: SlotSelection
): WorkerSlot =>
	typeof slotSelection === "object"
		? {
			workerId,
			slot: slotSelection.slot as DirectAssignmentSlot,
			workerShiftStartDateTime: slotSelection.customStartTime,
			workerShiftEndDateTime: slotSelection.customEndTime,
		}
		: { workerId, slot: slotSelection as DirectAssignmentSlot };

interface UseDirectAssignPickerProps {
	workers: WorkersPropsV2[];
	shift: DirectAssignShift | null;
	initialSelection: SelectedWorkerSlot[];
	isWorkerSubTypeEnabled: boolean;
}

const useDirectAssignPicker = ({
	workers,
	shift,
	initialSelection,
	isWorkerSubTypeEnabled,
}: UseDirectAssignPickerProps) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [workersSelected, setWorkersSelected] = useState<GridRowId[]>(() =>
		initialSelection.map((selected) => selected.workerId).sort()
	);
	const [workerSlotSelections, setWorkerSlotSelections] = useState<{
		[workerId: string]: SlotSelection;
	}>(() =>
		Object.fromEntries(
			initialSelection.map((selected) => [
				selected.workerId,
				toSlotSelection(selected),
			])
		)
	);
	const [columnVisibility, setColumnVisibility] = useState<{
		[field: string]: boolean;
	}>({});

	const workersRequired = Number(shift?.numberOfWorkersNeeded ?? 0);

	const shiftMetadata = useMemo(
		() => (shift ? { ...shift, jobId: shift.jobId ?? null } : {}),
		[shift]
	);

	const rows: WorkersExtendedPropV2[] = useMemo(() => {
		if (!shift || !Array.isArray(workers)) return [];
		return workers
			.filter((worker) => !worker.status || PICKABLE_STATUSES.includes(worker.status))
			.map((worker, index) => ({
				...worker,
				id: worker.workerId,
				reason: worker.reason ?? worker.cancelReason ?? "",
				overtime: worker.workerFlexTime
					? worker.workerFlexTime
					: t("common_not-applicable"),
				overtimeMins: worker.workerFlexMinutes ? worker.workerFlexMinutes : 0,
				errorMessage: "",
				order: index,
				acceptanceOrder: index,
				fullTimeShifts: formatFullTimeShifts(
					filterShiftsByWorkerDay(
						worker.ftShifts,
						worker.workerTimeZone,
						shift
					),
					worker.shiftPatternName
				),
			}));
	}, [workers, shift, t]);

	// A Worker unticked in the grid gives up their slot too
	useEffect(() => {
		setWorkerSlotSelections((previous) => {
			const selectedIds = workersSelected.map(String);
			const kept = Object.fromEntries(
				Object.entries(previous).filter(([workerId]) =>
					selectedIds.includes(workerId)
				)
			);
			return Object.keys(kept).length === Object.keys(previous).length
				? previous
				: kept;
		});
	}, [workersSelected]);

	// Nobody holds a spot on a Shift that has not been published, so every
	// spot starts open and only this pick fills them
	const availableSlots = useMemo(() => {
		const taken = shift
			? countHalvesTaken(
				Object.entries(workerSlotSelections).map(([workerId, slotSelection]) =>
					toWorkerSlot(workerId, slotSelection)
				),
				shift
			)
			: { firstHalf: 0, secondHalf: 0 };
		const firstHalf = workersRequired - taken.firstHalf;
		const secondHalf = workersRequired - taken.secondHalf;
		return {
			fullShift: Math.max(0, Math.min(firstHalf, secondHalf)),
			firstHalf: Math.max(0, firstHalf),
			secondHalf: Math.max(0, secondHalf),
		};
	}, [workerSlotSelections, workersRequired, shift]);

	const handleWorkerSlotChange = useCallback(
		(
			workerId: string,
			slot: number,
			customStartTime?: string,
			customEndTime?: string
		) => {
			setWorkerSlotSelections((previous) => ({
				...previous,
				[workerId]:
					customStartTime && customEndTime
						? {
							slot,
							customStartTime: new Date(customStartTime).toISOString(),
							customEndTime: new Date(customEndTime).toISOString(),
						}
						: slot,
			}));
		},
		[]
	);

	// Give a newly ticked Worker a slot the same way the Shift Details Assign
	// step does: full Shift first, then first half, then second half
	useEffect(() => {
		if (!shift) return;
		const newlySelectedIds = workersSelected
			.map(String)
			.filter(
				(workerId) =>
					!Object.prototype.hasOwnProperty.call(workerSlotSelections, workerId)
			);
		if (newlySelectedIds.length === 0) return;

		const newSelections: { [workerId: string]: number } = {};
		newlySelectedIds.forEach((workerId) => {
			const worker = rows.find((row) => row.id === workerId);
			if (!worker) return;
			if (
				availableSlots.fullShift > 0 ||
				(!shift.isPartialSignupEnabled && !shift.isFlexiSignupEnabled)
			) {
				newSelections[workerId] = 0;
				return;
			}

			const workerStart = new Date(
				worker.workerShiftStartDateTime ||
					worker.ftShifts?.[0]?.StartDateTime ||
					shift.startDateTime
			);
			const isAvailableFromStart =
				differenceInMinutes(workerStart, new Date(shift.startDateTime)) <=
				(shift.overlappingSignupLimitMins || 0);
			if (availableSlots.firstHalf > 0 && isAvailableFromStart) {
				newSelections[workerId] = 1;
				return;
			}
			if (availableSlots.secondHalf > 0) {
				newSelections[workerId] = 2;
			}
		});
		if (Object.keys(newSelections).length > 0) {
			setWorkerSlotSelections((previous) => ({ ...previous, ...newSelections }));
		}
	}, [workersSelected, workerSlotSelections, availableSlots, rows, shift]);

	const openFlexiSlots = useMemo(() => [], []);

	// Only this pick is on a Shift that has not been published, so it alone
	// bounds how far a custom window can stretch
	const occupiedWindows = useMemo(
		() =>
			Object.entries(workerSlotSelections).map(([workerId, slotSelection]) =>
				toWorkerSlot(workerId, slotSelection)
			),
		[workerSlotSelections]
	);

	const { allColumns } = useShiftDetailsDialogColumns(
		shiftMetadata,
		0,
		rows.length,
		search,
		ASSIGN_SHIFT,
		shift?.shiftAllocationType ?? "",
		shift?.isShiftOpenForOtherLocation ?? false,
		isWorkerSubTypeEnabled,
		shift?.isOverlappigSignupEnabled ?? false,
		shift?.isPartialSignupEnabled ?? false,
		shift?.isFlexiSignupEnabled ?? false,
		shift?.overlappingSignupLimitMins ?? 0,
		workers,
		noop,
		0,
		0,
		0,
		workersRequired,
		workerSlotSelections,
		handleWorkerSlotChange,
		availableSlots,
		workersSelected,
		openFlexiSlots,
		undefined,
		occupiedWindows
	);

	const columnOptions = useMemo(
		() =>
			((Array.isArray(allColumns) ? allColumns : []) as PickerColumn[])
				.filter((column) => !HIDDEN_FIELDS.includes(column.field))
				.map((column) => ({
					...column,
					checked:
						columnVisibility[column.field] ??
						(UNCHECKED_BY_DEFAULT_FIELDS.includes(column.field)
							? false
							: column.checked),
				})),
		[allColumns, columnVisibility]
	);

	const setColumnOptions = useCallback(
		(options: { field?: string; checked: boolean }[]) => {
			setColumnVisibility(
				Object.fromEntries(
					options
						.filter((option) => !!option.field)
						.map((option) => [option.field as string, option.checked])
				)
			);
		},
		[]
	);

	const selection: SelectedWorkerSlot[] = useMemo(
		() =>
			workersSelected
				.map(String)
				.map((workerId) => {
					const row = rows.find((r) => r.id === workerId);
					return {
						...toWorkerSlot(workerId, workerSlotSelections[workerId] ?? 0),
						workerName: row?.workerName ?? "",
					};
				}),
		[workersSelected, workerSlotSelections, rows]
	);

	// No half may hold more than Required Workers, and a custom window must be
	// complete before it can be sent
	const isSelectionValid =
		!!shift &&
		isWithinRequiredWorkers(selection, shift) &&
		selection.every(
			(selected) =>
				selected.slot !== 3 ||
				(!!selected.workerShiftStartDateTime &&
					!!selected.workerShiftEndDateTime)
		);

	return {
		rows,
		search,
		setSearch,
		workersSelected,
		setWorkersSelected,
		columnOptions,
		setColumnOptions,
		shiftMetadata,
		workersRequired,
		// Like Shift Details, a Worker can be ticked while any half is open
		canSelectMore: availableSlots.firstHalf > 0 || availableSlots.secondHalf > 0,
		selection,
		isSelectionValid,
	};
};

export default useDirectAssignPicker;
